import { useCallback, useEffect, useState } from 'react';
import { Header, Input, List, Loader, Message } from 'semantic-ui-react';
import { Button } from '~/components';
import { usePromiseWatcher } from '~/hooks';
import {
  deletePasskey,
  generateBackupCodes,
  getTwoFactorStatus,
  registerPasskey,
} from '~/api/auth';

interface Passkey {
  id: string;
  deviceName: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

interface TwoFactorStatus {
  enabled: boolean;
  passkeys: Passkey[];
  backupCodesRemaining: number;
}

export default function SecuritySettings() {
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [deviceName, setDeviceName] = useState('');
  const [newCodes, setNewCodes] = useState<string[] | null>(null);

  const refresh = useCallback(async () => {
    setStatus(await getTwoFactorStatus());
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const { execute: addPasskey, status: addStatus } = usePromiseWatcher(
    async () => {
      await registerPasskey(deviceName.trim() || undefined);
      setDeviceName('');
      await refresh();
    },
    { messageStub: 'registering your passkey' },
    [deviceName]
  );

  const { execute: removePasskey } = usePromiseWatcher(
    async (id: string) => {
      await deletePasskey(id);
      await refresh();
    },
    { messageStub: 'removing the passkey' },
    []
  );

  const { execute: regenerate, status: regenStatus } = usePromiseWatcher(
    async () => {
      const { codes } = await generateBackupCodes();
      setNewCodes(codes);
      await refresh();
    },
    { messageStub: 'generating new backup codes', loading: null },
    []
  );

  if (loading) return <Loader active inline="centered" />;

  return (
    <div>
      <Header as="h2">Two-step verification</Header>
      <p>
        {status?.enabled
          ? 'Two-step verification is on for your account.'
          : 'Add a passkey to protect your account with two-step verification.'}
      </p>

      <Header as="h3">Passkeys</Header>
      {status?.passkeys.length ? (
        <List divided relaxed>
          {status.passkeys.map((p) => (
            <List.Item key={p.id}>
              <List.Content floated="right">
                <Button outlined size="small" onClick={() => removePasskey(p.id)}>
                  Remove
                </Button>
              </List.Content>
              <List.Icon name="key" size="large" verticalAlign="middle" />
              <List.Content>
                <List.Header>{p.deviceName || 'Passkey'}</List.Header>
                <List.Description>
                  Added {new Date(p.createdAt).toLocaleDateString()}
                  {p.lastUsedAt && ` · Last used ${new Date(p.lastUsedAt).toLocaleDateString()}`}
                </List.Description>
              </List.Content>
            </List.Item>
          ))}
        </List>
      ) : (
        <Message
          info
          size="tiny"
          content="You have no passkeys registered yet."
          style={{ marginBottom: 'var(--medium)' }}
        />
      )}

      <Input
        size="small"
        placeholder="Device name (optional)"
        value={deviceName}
        onChange={(_e, { value }) => setDeviceName(value)}
        style={{ marginRight: 'var(--small)' }}
      />
      <Button secondary size="small" loading={addStatus === 'pending'} onClick={addPasskey}>
        Add a passkey
      </Button>

      <Header as="h3" style={{ marginTop: 'var(--large)' }}>
        Backup codes
      </Header>
      <p>
        {status?.backupCodesRemaining ?? 0} unused backup code
        {status?.backupCodesRemaining === 1 ? '' : 's'} remaining. Regenerating replaces any
        existing codes.
      </p>
      {newCodes && (
        <Message warning style={{ marginBottom: 'var(--medium)' }}>
          <Message.Header>Save these codes now — they won&apos;t be shown again</Message.Header>
          <List>
            {newCodes.map((code) => (
              <List.Item key={code}>
                <code style={{ fontSize: '15px', letterSpacing: '2px' }}>{code}</code>
              </List.Item>
            ))}
          </List>
        </Message>
      )}
      <Button outlined size="small" loading={regenStatus === 'pending'} onClick={regenerate}>
        Regenerate backup codes
      </Button>
    </div>
  );
}
